import { useState } from 'react';
import { QBox, QFormControl, QDivider, QIcon, QLabelAction } from '@qualio/ui-components';
import { EventFieldProps, SubComponentProps, ValueObject, ViewModeDictionary } from './EventFormField.types';
import TextField from './components/TextField';
import NumericField from './components/NumericField';
import BooleanField from './components/BooleanField';
import DateField from './components/DateField';
import AttachmentField from './components/AttachmentField';
import RichTextField from './components/RichTextField';
import NonEditableRichText from '../NonEditableRichText/NonEditableRichText';
import DropdownField from './components/DropdownField/DropdownField';
import SectionField from './components/SectionField';
import CheckboxField from './components/CheckboxField';
import RadioField from './components/RadioField';
import ReadonlyWrapper from './components/ReadOnlyWrapper';
import SectionWrapper from './components/SectionWrapper';
import QObjectFormFieldLabel from './components/QObjectField/QObjectFormFieldLabel';
import QObjectFieldModal from './components/QObjectField/QObjectFieldModal';
import { FieldTypeSchema, FieldType } from '../../types/formFields/common';
import { fieldTypeIsQObjectType } from '../../utils/eventFormUtils';
import QObjectField from './components/QObjectField/QObjectField';

const visualComponents: readonly FieldType[] = [FieldTypeSchema.enum.section] as const;

const componentMap = {
  text: TextField,
  numeric: NumericField,
  boolean: BooleanField,
  date: DateField,
  attachment: AttachmentField,
  rich_text: RichTextField,
  dropdown: DropdownField,
  section: SectionField,
  checkbox: CheckboxField,
  radio: RadioField,
  document: QObjectField,
  event: QObjectField,
  supplier: QObjectField,
  user: QObjectField,
  change_request: QObjectField,
  registry: QObjectField,
  design_controls: QObjectField,
  training_plan: QObjectField,
  document_template: QObjectField,
  event_template: QObjectField,
  task: QObjectField,
} as const satisfies Record<FieldType, (props: SubComponentProps) => JSX.Element | null>;

const EventFormField = (props: EventFieldProps) => {
  const { helper, label, isRequired, inputType, options, multi, mode, resourceSubType, showAlertIcon } = props;

  const Component = componentMap[inputType];
  const isVisualComponent = visualComponents.includes(inputType);
  const isQObjectComponent = fieldTypeIsQObjectType(inputType);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  if (!Component) {
    throw new Error(`Attempted to use a component that is not implemented yet: ${inputType}`);
  }

  /**
   * Here we are setting the values and handlers to handle the different "modes"
   * we can have for the form fields. These modes are:
   * - PREVIEW: We use a local state and stateHandler as all values are ephemeral
   * - INTERACTIVE: We use the values and stateHandler given from the parent component
   * - READONLY: We use the values given from the parent component, but we use a local
   *    stateHandler to handle the "onChange" event. Because none of the subcomponents
   *    should allow changes.
   */
  const initialValueData = mode === ViewModeDictionary.PREVIEW || isVisualComponent ? [] : props.fieldValues;
  const [localValue, setLocalValue] = useState<ValueObject[]>(initialValueData);
  const fieldId = mode === ViewModeDictionary.PREVIEW ? 0 : props.fieldId;
  const fieldValues = mode !== ViewModeDictionary.INTERACTIVE ? localValue : props.fieldValues;
  const changeHandler = mode !== ViewModeDictionary.INTERACTIVE ? setLocalValue : props.onChange;
  const defaultContent = mode !== ViewModeDictionary.PREVIEW ? undefined : props.defaultContent;
  const showEditButton = isQObjectComponent && mode !== ViewModeDictionary.READONLY && fieldValues.length > 0;

  const childProps = {
    mode,
    label,
    fieldId,
    fieldValues,
    options,
    multi,
    defaultContent,
    inputType,
    onChange: changeHandler,
    isEditModalOpen,
    setIsEditModalOpen,
    resourceSubType,
  };

  if (!Component) {
    console.log('Attempted to use a component that is not implemented yet');
    return null;
  }

  if (isVisualComponent) {
    return (
      <QFormControl data-cy={props['data-cy']} isRequired={false}>
        <SectionWrapper>
          <Component {...childProps}>
            {showAlertIcon && (
              <QLabelAction>
                <QIcon iconName="AlertTriangle" color="yellow.500" size="sm" />
              </QLabelAction>
            )}
          </Component>
          <QDivider />
          <NonEditableRichText content={helper ?? ''} mode="READONLY" textColor="GrayText" />
        </SectionWrapper>
      </QFormControl>
    );
  }

  return (
    <>
      <QFormControl data-cy={props['data-cy']} label={isQObjectComponent ? undefined : label} isRequired={isRequired}>
        {!isQObjectComponent && showAlertIcon && (
          <QLabelAction>
            <QIcon iconName="AlertTriangle" color="yellow.500" size="sm" />
          </QLabelAction>
        )}
        {isQObjectComponent && (
          <QObjectFormFieldLabel
            label={label}
            showEditBtn={showEditButton}
            onEditBtnClick={setIsEditModalOpen}
            showAlertIcon={showAlertIcon}
            showRequired={isRequired}
          />
        )}
        <ReadonlyWrapper>
          <NonEditableRichText content={helper ?? ''} mode="READONLY" textColor="GrayText" />
        </ReadonlyWrapper>
        <QBox marginTop="8px">
          <Component {...childProps} />
        </QBox>
      </QFormControl>
      {/*
       * The QObject resource lookup modal is rendered as a sibling of the QFormControl, not a
       * child of it. Chakra v2's useRadio inherits its id from the nearest FormControlContext
       * (which flows through portals), so rendering the modal inside the form control collapsed
       * every QLookup radio onto the form control's single id and made them unselectable.
       */}
      {isQObjectComponent && <QObjectFieldModal {...childProps} />}
    </>
  );
};

export default EventFormField;
